import { INTERVALS, type IntervalName } from './notes';

export type ScaleTypeSlug =
  | 'major' | 'natural-minor' | 'major-pentatonic' | 'minor-pentatonic' | 'blues' | 'major-blues'
  | 'harmonic-minor' | 'melodic-minor' | 'dorian' | 'phrygian' | 'lydian' | 'mixolydian' | 'locrian'
  | 'phrygian-dominant';

export type Tonality = 'major' | 'minor';

export interface ScaleType {
  slug: ScaleTypeSlug;
  nameEn: string;
  nameFa: string;
  formula: readonly IntervalName[];
  tonality: Tonality;
  /** Extra accepted search words (the English and Persian names are implied). */
  aliases: readonly string[];
  /** Blues types borrow the positions and spelling of a pentatonic parent and add one blue note. */
  parent?: { type: ScaleTypeSlug; blue: IntervalName };
}

export const TONALITY_FA: Readonly<Record<Tonality, string>> = { major: 'ماژور', minor: 'مینور' };

/** Tonality rule (spec 3.2): major when the formula contains a major third, otherwise minor. */
const tonalityOf = (formula: readonly IntervalName[]): Tonality => (formula.includes('3') ? 'major' : 'minor');

type RawType = Omit<ScaleType, 'tonality'>;

const RAW: readonly RawType[] = [
  { slug: 'major', nameEn: 'Major (Ionian)', nameFa: 'ماژور (یونین)', formula: ['1', '2', '3', '4', '5', '6', '7'], aliases: ['ionian'] },
  { slug: 'natural-minor', nameEn: 'Natural Minor (Aeolian)', nameFa: 'مینور طبیعی (ائولین)', formula: ['1', '2', 'b3', '4', '5', 'b6', 'b7'], aliases: ['aeolian'] },
  { slug: 'major-pentatonic', nameEn: 'Major Pentatonic', nameFa: 'پنتاتونیک ماژور', formula: ['1', '2', '3', '5', '6'], aliases: ['pentatonic'] },
  { slug: 'minor-pentatonic', nameEn: 'Minor Pentatonic', nameFa: 'پنتاتونیک مینور', formula: ['1', 'b3', '4', '5', 'b7'], aliases: ['pentatonic'] },
  { slug: 'blues', nameEn: 'Blues', nameFa: 'بلوز', formula: ['1', 'b3', '4', 'b5', '5', 'b7'], aliases: [], parent: { type: 'minor-pentatonic', blue: 'b5' } },
  { slug: 'major-blues', nameEn: 'Major Blues', nameFa: 'بلوز ماژور', formula: ['1', '2', 'b3', '3', '5', '6'], aliases: [], parent: { type: 'major-pentatonic', blue: 'b3' } },
  { slug: 'harmonic-minor', nameEn: 'Harmonic Minor', nameFa: 'مینور هارمونیک', formula: ['1', '2', 'b3', '4', '5', 'b6', '7'], aliases: [] },
  { slug: 'melodic-minor', nameEn: 'Melodic Minor', nameFa: 'مینور ملودیک', formula: ['1', '2', 'b3', '4', '5', '6', '7'], aliases: [] },
  { slug: 'dorian', nameEn: 'Dorian', nameFa: 'دورین', formula: ['1', '2', 'b3', '4', '5', '6', 'b7'], aliases: [] },
  { slug: 'phrygian', nameEn: 'Phrygian', nameFa: 'فریجین', formula: ['1', 'b2', 'b3', '4', '5', 'b6', 'b7'], aliases: [] },
  { slug: 'lydian', nameEn: 'Lydian', nameFa: 'لیدین', formula: ['1', '2', '3', '#4', '5', '6', '7'], aliases: [] },
  { slug: 'mixolydian', nameEn: 'Mixolydian', nameFa: 'میکسولیدین', formula: ['1', '2', '3', '4', '5', '6', 'b7'], aliases: [] },
  { slug: 'locrian', nameEn: 'Locrian', nameFa: 'لوکرین', formula: ['1', 'b2', 'b3', '4', 'b5', 'b6', 'b7'], aliases: [] },
  { slug: 'phrygian-dominant', nameEn: 'Phrygian Dominant', nameFa: 'فریجین دومینانت', formula: ['1', 'b2', '3', '4', '5', 'b6', 'b7'], aliases: ['spanish', 'freygish'] }
];

/** 14 scale types. Table order is the canonical order (AC-28). */
export const SCALE_TYPES: readonly ScaleType[] = RAW.map((t) => ({ ...t, tonality: tonalityOf(t.formula) }));

const BY_SLUG = new Map<string, ScaleType>(SCALE_TYPES.map((t) => [t.slug, t]));

export const getScaleType = (slug: string): ScaleType | undefined => BY_SLUG.get(slug);

export const isHeptatonic = (t: ScaleType): boolean => t.formula.length === 7;

/** Semitones above the root for a formula entry. */
export const semis = (i: IntervalName): number => INTERVALS[i].semitones;
